import { Component, OnInit, Input, ViewEncapsulation, inject } from "@angular/core";
import { NgComponentOutlet } from "@angular/common";
import { CustomFieldBase } from "../custom-field-base";
import { GenericListConfig } from "@app/shared/models/GenericList/GenericList";
import { nameof } from "@methods/jeffs-toolkit";
import { ReactiveFormsModule, Validators, FormControl, AbstractControl, FormGroup } from "@angular/forms";
import { FormValidationHelper } from "@app/shared/helpers/form-validation-helper";
import { GenericListFieldType } from "@app/shared/models/GenericList/GenericListField";
import { GENERIC_LIST_MANAGER } from "@app/shared/generic-managers/generic-list-manager/generic-list-manager.token";
import {
    CustomFieldStandingDataModel,
    CustomFieldOptionModel,
} from "@models/customfields/CustomFieldStandingDataModel";
import { IdNameCombination } from "@models/generic/IdNameCombination";
import { CustomFieldType } from "@enums/customfields/CustomFieldType";
import { StandingDataField } from "@app/shared/models/GenericList/GenericStandingDataConfig";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { ToastrService } from "ngx-toastr";
import { firstValueFrom } from "rxjs";
import { TranslocoPipe, TranslocoService, provideTranslocoScope } from "@jsverse/transloco";
import { NgSelectModule } from "@ng-select/ng-select";
import { CheckboxComponent } from "@app/shared/cerrix-checkbox/cerrix-checkbox.component";
import { InputTooltipDirective } from "@app/shared/directives/input-tooltip.directive";

const CUSTOM_FIELD_EDITOR_SCOPE =
    "shared/generic-managers/generic-field-editor/custom-field-types/custom-field-editor";

@Component({
    selector: "custom-field-editor",
    templateUrl: "./custom-field-editor.component.html",
    styleUrls: ["./custom-field-editor.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [
        TranslocoPipe,
        ReactiveFormsModule,
        NgSelectModule,
        CheckboxComponent,
        NgComponentOutlet,
        InputTooltipDirective,
    ],
    providers: [
        provideTranslocoScope({
            scope: CUSTOM_FIELD_EDITOR_SCOPE,
            alias: "customFieldEditor",
        }),
    ],
})
export class CustomFieldEditor extends CustomFieldBase implements OnInit {
    private sdService = inject(StandingdataDataService);
    private toastr = inject(ToastrService);
    private transloco = inject(TranslocoService);
    protected readonly listManagerType = inject(GENERIC_LIST_MANAGER);

    @Input() field: StandingDataField;
    @Input() standingDataType?: number;

    mainFormGroup: FormGroup;
    listConfig: GenericListConfig;
    fieldTypes: IdNameCombination[];

    public get showEditor(): boolean {
        return (
            this.listConfig &&
            this.row &&
            [CustomFieldType.SingleSelect, CustomFieldType.MultiSelect].indexOf(
                this.row.fieldType,
            ) > -1
        );
    }

    /**
     * `listConfig` is created in `ngOnInit`, before the first `transloco` pipe of this scope
     * renders, so the list name is resolved by the pipe in the template and applied here.
     * The same config instance is returned, which keeps the binding stable.
     */
    public listConfigNamed(name: string): GenericListConfig {
        this.listConfig.name = name;
        return this.listConfig;
    }

    private get scopeLang(): string {
        return `${CUSTOM_FIELD_EDITOR_SCOPE}/${this.transloco.getActiveLang()}`;
    }

    private get row(): CustomFieldStandingDataModel {
        return <CustomFieldStandingDataModel>this.value;
    }

    ngOnInit() {
        if (!this.value || !this.value.ID) {
            this.value = <CustomFieldStandingDataModel>{
                visible: false,
                required: false,
                fieldTypeChangeAllowed: true,
            };
        }

        // Start the scope load here, not only from the template's pipe, so
        // `customFieldValidation` below (synchronous, cannot await) does not depend on a
        // pipe rendering first. Safe because the scope is in `config.scopeMapping`
        // (shared.module.ts), so the alias resolves the same way no matter which caller
        // loads the scope first.
        this.transloco.load(this.scopeLang).subscribe();

        this.fieldTypes = this.getCustomFieldTypes();
        this.setupFields();
    }

    private setupFields(): void {
        this.mainFormGroup = new FormGroup(this.getFormGroupControls());

        this.mainFormGroup.valueChanges.subscribe((x) => {
            FormValidationHelper.mapToModel(this.mainFormGroup, this.value);

            this.valueChange.emit(this.value);
        });

        if (!this.row.answerOptions) {
            this.row.answerOptions = [];
        }

        this.listConfig = <GenericListConfig>{
            isSortable: true,
            allowAdd: true,
            allowEdit: true,
            allowDelete: true,
            idProp: nameof<CustomFieldOptionModel>((x) => x.ID),
            sortProp: nameof<CustomFieldOptionModel>((x) => x.Sort_Order),
            data: this.row.answerOptions,

            fields: [
                {
                    fieldName: nameof<CustomFieldOptionModel>((x) => x.Name),
                    fieldType: GenericListFieldType.Text,
                    required: true,
                    unique: true,
                },
                {
                    fieldName: nameof<CustomFieldOptionModel>((x) => x.Value),
                    fieldType: GenericListFieldType.Number,
                },
                {
                    fieldName: nameof<CustomFieldOptionModel>((x) => x.Color),
                    fieldType: GenericListFieldType.ColorPicker,
                },
            ],

            dataChanged: (data: CustomFieldOptionModel[]) => {
                this.row.answerOptions = data;
            },

            preDeleteCheck: async (option: CustomFieldOptionModel) => {
                const optionInUse = await firstValueFrom(
                    this.sdService.checkIsOptionInUse(this.standingDataType, option.ID),
                );

                if (optionInUse) {
                    const scopeLang = this.scopeLang;
                    await firstValueFrom(this.transloco.load(scopeLang));

                    this.toastr.error(
                        this.transloco.translate("optionInUse", { name: option.Name }, scopeLang),
                    );
                    return false;
                }

                return true;
            },
        };

        this.field.customFieldValidation = () => {
            FormValidationHelper.markAllAsTouched(this.mainFormGroup);

            const validationErrors = FormValidationHelper.getFormControlErrors(this.mainFormGroup);
            const validationMessages = FormValidationHelper.getValidationMessages(
                validationErrors,
                [],
            );

            if (
                this.showEditor &&
                (!this.row.answerOptions || this.row.answerOptions.length === 0)
            ) {
                validationMessages.push(
                    this.transloco.translate("answerOptionRequired", {}, this.scopeLang),
                );
            }

            // Remove styling since it messes with toastr in standing-data-editor.component
            return validationMessages.distinct().map((msg) => {
                return (msg = msg.replace("<br> -", ""));
            });
        };
    }

    private getFormGroupControls(): { [p: string]: AbstractControl } {
        const formGroupControls: { [p: string]: AbstractControl } = {};

        formGroupControls[nameof<CustomFieldStandingDataModel>((c) => c.Name)] = new FormControl(
            this.row.Name,
            [Validators.required],
        );

        formGroupControls[nameof<CustomFieldStandingDataModel>((c) => c.required)] =
            new FormControl(this.row.required);

        formGroupControls[nameof<CustomFieldStandingDataModel>((c) => c.visible)] = new FormControl(
            this.row.visible,
        );

        formGroupControls[nameof<CustomFieldStandingDataModel>((c) => c.fieldType)] =
            new FormControl(
                { value: this.row.fieldType, disabled: !this.row.fieldTypeChangeAllowed },
                [Validators.required],
            );

        return formGroupControls;
    }

    private getCustomFieldTypes(): IdNameCombination[] {
        const customFieldTypes: IdNameCombination[] = [];
        for (const enumMember in CustomFieldType) {
            if (parseInt(enumMember, 10) > 0) {
                // Skip default option
                customFieldTypes.push(<IdNameCombination>{
                    ID: +enumMember,
                    Name: CustomFieldType[CustomFieldType[enumMember] + "Description"],
                });
            }
        }

        return customFieldTypes;
    }
}
